import { LANGUAGES, type SimulationDifficulty, type SimulationScenario } from "@scoach/types";
import { Brain, Globe } from "@scoach/ui/icons";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { ensureGeminiKey } from "../../lib/geminiKey.ts";
import { useAuthStore } from "../auth/store.ts";
import { companyApi } from "../company/api.ts";
import { dashboardApi } from "../dashboard/api.ts";
import { generateCompanyScenarios } from "./generateScenarios.ts";

type DifficultyFilter = "all" | SimulationDifficulty;

const DIFFICULTY_STARS: Record<SimulationDifficulty, string> = {
  beginner: "★",
  intermediate: "★★",
  advanced: "★★★",
};

const DIFFICULTY_LABELS: Record<SimulationDifficulty, string> = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

export function SimulationTraining() {
  const nav = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [filter, setFilter] = useState<DifficultyFilter>("all");
  const [startingId, setStartingId] = useState<string | null>(null);
  const [orgData, setOrgData] = useState<import("@scoach/types").Organization | null>(null);
  const simulationLanguage = useAuthStore((s) => s.simulationLanguage);
  const setSimulationLanguage = useAuthStore((s) => s.setSimulationLanguage);

  useEffect(() => {
    companyApi
      .getMyOrganization()
      .then((res) => {
        if (res?.organization) setOrgData(res.organization);
      })
      .catch(() => {});
  }, []);

  const companyScenarios = useMemo(() => {
    if (orgData) {
      return generateCompanyScenarios(orgData);
    }
    if (user?.companyName) {
      return generateCompanyScenarios({ name: user.companyName });
    }
    return generateCompanyScenarios(null);
  }, [orgData, user?.companyName]);

  const activeCompanyName = orgData?.name || user?.companyName || "Your Company";

  const filtered = useMemo(() => {
    if (filter === "all") return companyScenarios;
    return companyScenarios.filter((s) => s.difficulty === filter);
  }, [filter, companyScenarios]);

  async function startScenario(scenario: SimulationScenario) {
    await ensureGeminiKey();
    setStartingId(scenario.id);
    try {
      const meeting = await dashboardApi.createScenarioSimulation(scenario.id, scenario);
      nav({ to: "/meetings/$id/live", params: { id: meeting.id } });
    } catch {
      alert("Failed to create simulation. Please try again.");
    } finally {
      setStartingId(null);
    }
  }

  return (
    <section className="sim-training">
      <div className="sim-training-head">
        <div>
          <h2 className="dash-h2">
            <Brain size={18} /> Simulation Training
          </h2>
          <p className="sim-training-sub">
            Practice sales conversations with AI clients tailored to {activeCompanyName}
          </p>
        </div>
        <div className="sim-training-controls">
          <div className="seg seg-sm">
            <button
              type="button"
              className={filter === "all" ? "on" : ""}
              onClick={() => setFilter("all")}
            >
              All
            </button>
            <button
              type="button"
              className={filter === "beginner" ? "on" : ""}
              onClick={() => setFilter("beginner")}
            >
              Beginner
            </button>
            <button
              type="button"
              className={filter === "intermediate" ? "on" : ""}
              onClick={() => setFilter("intermediate")}
            >
              Intermediate
            </button>
            <button
              type="button"
              className={filter === "advanced" ? "on" : ""}
              onClick={() => setFilter("advanced")}
            >
              Advanced
            </button>
          </div>
        </div>
      </div>

      <div className="sim-cards">
        {filtered.map((s) => (
          <div key={s.id} className={`sim-card sim-card-${s.difficulty}`}>
            <div className={`sim-card-difficulty diff-${s.difficulty}`}>
              {DIFFICULTY_STARS[s.difficulty]}
              <span className="diff-label">{DIFFICULTY_LABELS[s.difficulty]}</span>
            </div>
            <div className="sim-card-title">{s.title}</div>
            <div className="sim-card-subtitle">{s.subtitle}</div>
            <div className="sim-card-persona">
              <div className="sim-card-persona-name">{s.persona}</div>
              <div className="sim-card-persona-role">
                {s.personaRole}, {s.company}
              </div>
            </div>
            <div className="sim-card-tags">
              {s.gcpFocus.slice(0, 3).map((tag) => (
                <span key={tag} className="sim-tag">
                  {tag}
                </span>
              ))}
            </div>
            <div className="sim-card-foot">
              <label className="sim-card-lang" title="Language the AI client speaks in">
                <Globe size={13} />
                <select
                  value={simulationLanguage}
                  onChange={(e) =>
                    setSimulationLanguage(e.target.value as typeof simulationLanguage)
                  }
                >
                  {LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="pill-btn primary sim-card-start"
                disabled={startingId === s.id}
                onClick={() => startScenario(s)}
              >
                {startingId === s.id ? "Starting…" : "▶ Start"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
